'use client'

import { useEffect, useState } from 'react'

const DISMISS_KEY = 'almaz:ios-install-dismissed-at'
const REMIND_AFTER_MS = 14 * 24 * 60 * 60 * 1000

function isStandalone(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches ||
    Boolean((navigator as Navigator & { standalone?: boolean }).standalone)
}

export function shouldShowIosInstallPrompt({
  userAgent,
  standalone,
  dismissedAt,
  now = Date.now(),
}: {
  userAgent: string
  standalone: boolean
  dismissedAt: number
  now?: number
}): boolean {
  const ua = userAgent
  const iphone = /iPhone|iPod/i.test(ua)
  const safari = /Safari/i.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua)
  const recentlyDismissed = dismissedAt > 0 && now - dismissedAt < REMIND_AFTER_MS
  return iphone && safari && !standalone && !recentlyDismissed
}

export default function IosInstallPrompt() {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(() => {})
    }
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0)
    if (!shouldShowIosInstallPrompt({
      userAgent: navigator.userAgent,
      standalone: isStandalone(),
      dismissedAt,
    })) return
    const timer = window.setTimeout(() => setOpen(true), 900)
    return () => window.clearTimeout(timer)
  }, [])

  function dismiss() {
    localStorage.setItem(DISMISS_KEY, String(Date.now()))
    setOpen(false)
  }

  if (!open) return null
  return (
    <div className="md:hidden fixed inset-0 z-[80] flex items-end" role="dialog" aria-modal="true" aria-labelledby="ios-install-title">
      <button className="absolute inset-0 bg-onyx/35 backdrop-blur-[1px]" onClick={dismiss} aria-label="Закрыть подсказку" />
      <section className="relative w-full rounded-t-[22px] bg-paper px-5 pt-3 pb-[calc(20px+env(safe-area-inset-bottom))] shadow-2xl">
        <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-rule-strong" />
        <button onClick={dismiss} className="absolute right-3 top-3 size-11 inline-flex items-center justify-center text-[23px] text-ink-faint" aria-label="Закрыть">×</button>
        <div className="pr-10">
          <div className="flex items-center gap-3">
            <div className="size-11 rounded-xl bg-onyx text-ivory flex items-center justify-center font-semibold text-[18px]">А</div>
            <div>
              <h2 id="ios-install-title" className="text-[17px] font-semibold text-ink">Добавьте ORI на экран «Домой»</h2>
              <p className="mt-0.5 text-[11px] text-ink-muted">Будет открываться как отдельное приложение.</p>
            </div>
          </div>
          <ol className="mt-5 space-y-3">
            <li className="flex items-center gap-3 text-[13px]"><span className="size-7 rounded-full bg-bone flex items-center justify-center text-[11px] font-semibold">1</span><span>В Safari нажмите кнопку <b>«Поделиться»</b> <span className="inline-block align-[-3px] text-st-delivery"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 16V3m0 0L8 7m4-4 4 4"/><path d="M5 10v9h14v-9"/></svg></span></span></li>
            <li className="flex items-center gap-3 text-[13px]"><span className="size-7 rounded-full bg-bone flex items-center justify-center text-[11px] font-semibold">2</span><span>Прокрутите меню и выберите <b>«На экран „Домой“»</b>.</span></li>
            <li className="flex items-center gap-3 text-[13px]"><span className="size-7 rounded-full bg-bone flex items-center justify-center text-[11px] font-semibold">3</span><span>Нажмите <b>«Добавить»</b> в правом верхнем углу.</span></li>
          </ol>
          <button onClick={dismiss} className="btn btn-primary w-full mt-5">Понятно</button>
        </div>
      </section>
    </div>
  )
}
